import React from "react";

import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  query,
  updateDoc,
  where,
} from "firebase/firestore";

import app from "../firebase";

import { getStorage } from "firebase/storage";
import { CafeteriaType, ReportStatusType, ReportType } from "../../types/database";
import { Dev } from "../util/consoleUtil";
import firebaseError from "../util/firebaseError";

const db = getFirestore(app);
const storage = getStorage(app);

export const CafeteriaService = {
  One: () => {
    const [isLoading, setLoading] = React.useState(false);

    const [item, setItem] = React.useState<ReportType | null>(null);

    const runRequest = async (itemId: string) => {
      return new Promise(async (Ok, Rej) => {
        try {
          setLoading(true);
          const mainRef = doc(db, "reports", itemId);
          const mainSnap = await getDoc(mainRef);

          const itemObj = { id: itemId, ...mainSnap.data() } as ReportType;
          setLoading(false);

          if (!mainSnap.exists()) return Rej(new Error("Item não existe."));

          setItem(itemObj);

          Ok(itemObj);
        } catch (error) {
          console.log(...new Dev("Request Error [one]", error).Error());
          setLoading(false);
          Rej(new Error("Erro ao obter dados. " + firebaseError(error)));
        }
      });
    };

    return { item, isLoading, runRequest };
  },

  List: () => {
    const [isLoading, setLoading] = React.useState(false);

    const [items, setItems] = React.useState<CafeteriaType[]>([]);

    const runRequest = async (locationId: string) => {
      return new Promise(async (Ok, Rej) => {
        try {
          setLoading(true);

          const mainRef = collection(db, "cafeterias");
          const queryRef = query(
            mainRef,
            where("enabled", "==", true),
            where("locationId", "array-contains", locationId)
          );
          const mainSnap = await getDocs(queryRef);
          const listData = [] as any;

          mainSnap.forEach((doc) => {
            listData.push({ id: doc.id, ...doc.data() });
          });

          setItems(listData);

          setLoading(false);
          Ok(listData);
        } catch (error) {
          console.log(...new Dev("Request Error [list]", error).Error());
          setLoading(false);
          Rej(new Error("Erro ao obter dados. " + firebaseError(error)));
        }
      });
    };

    return { items, isLoading, runRequest };
  },

  OneFromUser: () => {
    const [isLoading, setLoading] = React.useState(false);

    const [item, setItem] = React.useState<CafeteriaType | null>(null);

    const runRequest = async (userId: string) => {
      return new Promise<CafeteriaType | null>(async (Ok, Rej) => {
        try {
          setLoading(true);

          const mainRef = collection(db, "cafeterias");
          const queryRef = query(mainRef, where("ownerId", "==", userId));
          const mainSnap = await getDocs(queryRef);
          const listData = [] as any;

          mainSnap.forEach((doc) => {
            listData.push({ id: doc.id, ...doc.data() });
          });

          setItem(listData?.[0] || null);

          setLoading(false);
          Ok(listData?.[0] || null);
        } catch (error) {
          console.log(...new Dev("Request Error [listFromUser]", error).Error());
          setLoading(false);
          Rej(new Error("Erro ao obter dados. " + firebaseError(error)));
        }
      });
    };

    return { item, isLoading, runRequest };
  },

  ListStatus: () => {
    const [isLoading, setLoading] = React.useState(false);

    const [items, setItems] = React.useState<any[] | null>(null);

    const runRequest = async (status: ReportStatusType) => {
      return new Promise(async (Ok, Rej) => {
        try {
          setLoading(true);

          const mainRef = collection(db, "reports");
          const queryRef = query(mainRef, where("status", "==", status));
          const mainSnap = await getDocs(queryRef);
          const listData = [] as any;

          mainSnap.forEach((doc) => {
            listData.push({ id: doc.id, ...doc.data() });
          });

          setItems(listData);

          setLoading(false);
          Ok(listData);
        } catch (error) {
          console.log(...new Dev("Request Error [listStatus]", error).Error());
          setLoading(false);
          Rej(new Error("Erro ao obter dados. " + firebaseError(error)));
        }
      });
    };

    return { items, isLoading, runRequest };
  },

  Create: () => {
    const [isLoading, setLoading] = React.useState(false);

    const runRequest = async (item: ReportType) => {
      return new Promise(async (Ok, Rej) => {
        try {
          setLoading(true);

          const mainRef = collection(db, "reports");
          const mainSnap = await addDoc(mainRef, item);

          setLoading(false);
          Ok(true);
        } catch (error) {
          console.log(...new Dev("Request Error [create]", error).Error());
          setLoading(false);
          Rej(new Error("Erro ao criar dados. " + firebaseError(error)));
        }
      });
    };

    return { isLoading, runRequest };
  },

  Delete: () => {
    const [isLoading, setLoading] = React.useState(false);

    const runRequest = async (itemId: string) => {
      return new Promise(async (Ok, Rej) => {
        try {
          setLoading(true);

          const mainRef = doc(db, "reports", itemId);
          const mainSnap = await deleteDoc(mainRef);

          setLoading(false);
          Ok(true);
        } catch (error) {
          console.log(...new Dev("Request Error [delete]", error).Error());
          setLoading(false);
          Rej(new Error("Erro ao remover dados. " + firebaseError(error)));
        }
      });
    };

    return { isLoading, runRequest };
  },

  Update: () => {
    const [isLoading, setLoading] = React.useState(false);

    const runRequest = async (itemId: string, payload: any) => {
      return new Promise(async (Ok, Rej) => {
        try {
          setLoading(true);

          const mainRef = doc(db, "cafeterias", itemId);
          const mainSnap = await updateDoc(mainRef, payload);

          setLoading(false);
          Ok(true);
        } catch (error) {
          console.log(...new Dev("Request Error [update]", error).Error());
          setLoading(false);
          Rej(new Error("Erro ao atualizar dados. " + firebaseError(error)));
        }
      });
    };

    return { isLoading, runRequest };
  },

  UpdateArrayAdd: () => {
    const [isLoading, setLoading] = React.useState(false);

    const runRequest = async (itemId: string, key: string, payload: any) => {
      return new Promise(async (Ok, Rej) => {
        try {
          setLoading(true);

          const mainRef = doc(db, "reports", itemId);
          const mainSnap = await updateDoc(mainRef, { [key]: arrayUnion(payload) });

          setLoading(false);
          Ok(true);
        } catch (error) {
          console.log(...new Dev("Request Error [updateArrayAdd]", error).Error());
          setLoading(false);
          Rej(new Error("Erro ao atualizar dados. " + firebaseError(error)));
        }
      });
    };

    return { isLoading, runRequest };
  },

  UpdateArrayRemove: () => {
    const [isLoading, setLoading] = React.useState(false);

    const runRequest = async (itemId: string, key: string, payload: any) => {
      return new Promise(async (Ok, Rej) => {
        try {
          setLoading(true);

          const mainRef = doc(db, "reports", itemId);
          const mainSnap = await updateDoc(mainRef, { [key]: arrayRemove(payload) });

          setLoading(false);
          Ok(true);
        } catch (error) {
          console.log(...new Dev("Request Error [updateArrayRemove]", error).Error());
          setLoading(false);
          Rej(new Error("Erro ao atualizar dados. " + firebaseError(error)));
        }
      });
    };

    return { isLoading, runRequest };
  },
};
